import { motion } from "framer-motion";
import { MapPin, Users, Camera, Clock } from "lucide-react";
import PictureImage from "./PictureImage";
import { handwritten } from "./editorial";

export default function ValuePropSplit() {
  return (
    <section className="w-full py-24 md:py-36 px-6 md:px-12 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        {/* chapter header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mb-14 max-w-3xl"
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-salmon whitespace-nowrap">
              02: Show up · set up
            </span>
            <span className="flex-1 h-px bg-ink/15" />
          </div>
          <p
            className="text-2xl md:text-3xl text-salmon -rotate-1 mb-2"
            style={handwritten}
          >
            here&rsquo;s the whole idea
          </p>
          <h2 className="text-4xl md:text-6xl font-serif tracking-tight mb-5 leading-[1.05]">
            Show up to one.
            <br />
            <span className="italic text-salmon">Or set one up.</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-700 max-w-2xl leading-relaxed">
            Someone else has already done the planning. Or you do the planning,
            and the right people loop in. Either way, you end up actually
            meeting people.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* "Show up" — polaroid-framed */}
          <motion.div
            initial={{ opacity: 0, x: -30, rotate: -4 }}
            whileInView={{ opacity: 1, x: 0, rotate: -2 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            whileHover={{ rotate: 0 }}
            className="relative polaroid-frame p-3 pb-10 border shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)] transform-gpu"
          >
            <div className="h-72 md:h-96 w-full relative overflow-hidden bg-paper-salmon">
              <PictureImage
                src="/hero-friends.jpg"
                alt="People meeting up at a loop"
                width={1080}
                height={720}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute top-5 left-5 bg-canvas border border-ink/15 text-ink px-3 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1">
                <MapPin size={14} />
                Hackney, London
              </div>
              <div className="absolute bottom-5 left-5 bg-ink text-canvas px-4 py-2 rounded-full font-bold text-lg flex items-center gap-2">
                <Users size={16} />4 looped in
              </div>
            </div>
            <p
              className="mt-3 text-center text-xl text-gray-600"
              style={handwritten}
            >
              show up
            </p>
          </motion.div>

          {/* "Set up" — hard editorial card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
            className="relative bg-canvas border border-ink/15 rounded-3xl p-8 md:p-10 shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)]"
          >
            <p
              className="absolute -top-7 -right-3 text-3xl text-salmon -rotate-6 hidden md:block"
              style={handwritten}
            >
              set up
            </p>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-2xl font-serif tracking-tight text-ink">
                Three taps to post
              </h3>
              <span
                className="text-ink px-3 py-1 rounded-full font-bold text-sm shadow-[0_4px_16px_-6px_rgba(255,160,122,0.45)]"
                style={{
                  background:
                    "linear-gradient(135deg, #ffd4b0 0%, #fff8f4 35%, #e8f8f7 65%, #9dd9d6 100%)",
                }}
              >
                ~30 sec
              </span>
            </div>

            <div className="h-px w-full bg-ink/15 my-6" />

            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-gray-500">
                  <Camera size={20} className="text-salmon" />
                  <span className="font-medium text-ink">Step 1</span>
                </div>
                <span className="bg-ink text-canvas px-3 py-1 rounded-full text-xs font-bold">
                  Add a photo
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-gray-500">
                  <MapPin size={20} className="text-salmon" />
                  <span className="font-medium text-ink">Step 2</span>
                </div>
                <span className="bg-ink/5 text-gray-600 px-3 py-1 rounded-full text-xs font-medium">
                  Drop a pin
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-gray-500">
                  <Clock size={20} className="text-salmon" />
                  <span className="font-medium text-ink">Step 3</span>
                </div>
                <span className="bg-ink/5 text-gray-600 px-3 py-1 rounded-full text-xs font-medium">
                  Say what time
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
